import { type ConfigStatusResp } from 'shared/api/analytics'
import { type EnhancedSignalsResp } from 'shared/api/biddingSignals'
import { type StandardReportResp } from 'shared/api/standardReports'
import { type CirrusSourceOption, getCirrusOptionId, isCirrusStandardReportKind } from 'shared/copilot/cirrusSource'
import { getStatusFromAPIStatusData, getStatusMarketingEvaluation, STATUSES } from 'shared/configStatuses'

/** Everything the shared status tooltip needs, straight off the config's status row. */
const toStatusDetail = (
  status: ConfigStatusResp | undefined,
  targetProjectId?: string | null
): CirrusSourceOption['statusDetail'] => ({
  lastUpdated: status?.lastUpdated,
  errorCode: status?.errorCode,
  errorMessage: status?.errorMessage,
  targetProjectId
})

/** Config statuses come back as a flat list keyed by a stringified config id. */
export const toConfigStatusMap = (statuses: readonly ConfigStatusResp[] = []): Record<string, ConfigStatusResp> =>
  Object.fromEntries(statuses.map((status) => [status.configId, status]))

/**
 * Recommendations is config-less — exactly as it works in the Flow UI today — so it is a
 * single leaf option rather than a list of configs.
 */
export const buildRecommendationsCirrusOption = (label: string): CirrusSourceOption => ({
  id: getCirrusOptionId('event_feed', null),
  collection: 'event_feed',
  configId: null,
  label
})

/**
 * Reporting-solution templates the agent can answer on today. The template id is sent
 * verbatim as `kind`, so this list is both the supported-template gate and the mapping —
 * there is no second vocabulary to keep in sync with Flow.
 *
 * A solution config carries exactly one template, so every picker entry maps to exactly one
 * `kind`. Solutions on any other template render as a disabled "coming soon" row: a
 * `standard_reports` source without a `kind` would let the agent reach for reports the
 * client's config never provisioned. Enabling another template is one line in
 * `CIRRUS_STANDARD_REPORT_KINDS` — plus the matching entry in Flow's `source_kinds`.
 */
export const buildReportingSolutionCirrusOptions = (
  configs: readonly StandardReportResp[],
  statusesByConfigId: Record<string, ConfigStatusResp> = {}
): CirrusSourceOption[] =>
  configs.map((config) => {
    const kind = isCirrusStandardReportKind(config.templateId) ? config.templateId : undefined
    // Match the Reporting Solutions list: descriptive name is the title users know;
    // `name` is the technical/table suffix. Fall back when descriptiveName is empty.
    const label = config.descriptiveName !== '' ? config.descriptiveName : config.name
    return {
      id: getCirrusOptionId('standard_reports', String(config.configId)),
      collection: 'standard_reports',
      configId: String(config.configId),
      kind,
      label,
      annotation: config.templateId,
      supported: kind != null,
      // Same derivation the Reporting Solutions list view uses, so the badge agrees with it.
      status: getStatusFromAPIStatusData(config.runSchedule, statusesByConfigId[String(config.configId)]?.status),
      statusDetail: toStatusDetail(statusesByConfigId[String(config.configId)], config.targetProjectId)
    }
  })

/**
 * Attribution and budget optimiser configs are shaped the same here, and both derive status
 * the marketing-evaluation way their list views do.
 */
interface NamedConfig {
  id: number
  name: string
  runSchedule: boolean
  targetProjectId?: string
}

const buildNamedConfigCirrusOptions = (
  collection: 'attribution' | 'bo',
  configs: readonly NamedConfig[],
  statusesByConfigId: Record<string, ConfigStatusResp>
): CirrusSourceOption[] =>
  configs.map((config) => {
    const status = statusesByConfigId[String(config.id)]
    return {
      id: getCirrusOptionId(collection, String(config.id)),
      collection,
      configId: String(config.id),
      label: config.name,
      status: getStatusMarketingEvaluation(config.runSchedule, status?.status, status?.errorCode),
      statusDetail: toStatusDetail(status, config.targetProjectId)
    }
  })

export const buildAttributionModelCirrusOptions = (
  configs: readonly NamedConfig[],
  statusesByConfigId: Record<string, ConfigStatusResp> = {}
): CirrusSourceOption[] => buildNamedConfigCirrusOptions('attribution', configs, statusesByConfigId)

export const buildBudgetOptimiserCirrusOptions = (
  configs: readonly NamedConfig[],
  statusesByConfigId: Record<string, ConfigStatusResp> = {}
): CirrusSourceOption[] => buildNamedConfigCirrusOptions('bo', configs, statusesByConfigId)

export const buildBiddingSignalCirrusOptions = (configs: readonly EnhancedSignalsResp[]): CirrusSourceOption[] =>
  configs
    .filter((config) => !config.deleted)
    .map((config) => ({
      id: getCirrusOptionId('enhanced_signals', String(config.id)),
      collection: 'enhanced_signals',
      configId: String(config.id),
      label: config.configName,
      // Bidding signals have no pipeline status — the list view shows enabled/paused.
      status: config.enabled ? STATUSES.active : STATUSES.paused
    }))

const byLabel = (left: CirrusSourceOption, right: CirrusSourceOption): number => left.label.localeCompare(right.label)

export const sortCirrusSourceOptions = (options: CirrusSourceOption[]): CirrusSourceOption[] =>
  [...options].sort(byLabel)

export const findCirrusSourceOption = (
  optionId: string | undefined,
  options: readonly CirrusSourceOption[]
): CirrusSourceOption | null => {
  const option = options.find((candidate) => candidate.id === optionId)
  return option == null || option.supported === false ? null : option
}

/**
 * Prefer the live picker option so chip tooltips stay current after status
 * refetches. Fall back to the pick-time snapshot if the option disappeared.
 */
export const resolveLiveCirrusSourceOption = (
  selected: CirrusSourceOption,
  liveOptions: readonly CirrusSourceOption[]
): CirrusSourceOption => findCirrusSourceOption(selected.id, liveOptions) ?? selected
